import React, { useEffect, useState } from 'react';

import ReconstructionAgent from '../components/ReconstructionAgent';
import PageMasthead from '../components/PageMasthead';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import EnterpriseSolutionModal from '../components/EnterpriseSolutionModal';
import { useRecoSession } from '../context/RecoSessionProvider';

/**
 * Dedicated home for the Reco intake chat, routed at /reco.
 * The same component that floats on the landing page, rendered inline so it
 * owns a full page instead of a corner.
 */
const ChatWithRecoPage: React.FC = () => {
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const { resetSession } = useRecoSession();

  // Always start fresh when navigating to /reco directly
  useEffect(() => { resetSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Exactly the viewport, never taller. The transcript inside the chat is the
   * only scroller on this page, so the header and footer stay in view and the
   * composer cannot drift below the fold as the conversation grows. dvh rather
   * than vh so a mobile URL bar does not push the footer out of reach.
   */
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-ink-950 text-ink-100">
      <SiteHeader />

      <main className="relative flex min-h-0 flex-1 flex-col">
        {/* A light source, so the field is not one flat rectangle. Wide and very
            faint — at 7% it should register as depth, not as a coloured panel. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[32rem] bg-[radial-gradient(60%_60%_at_50%_0%,rgba(12,180,228,0.07),transparent_70%)]"
        />

        {/* One column. The rails belong to the page, not the chat, so they run
            from the header to the footer and the masthead sits inside them
            rather than floating above a column it has no relationship to. */}
        <div className="relative mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col border-x border-ink-800">
          <div className="flex-shrink-0 border-b border-ink-800 px-4 pb-5 pt-6 sm:px-6 sm:pb-6 sm:pt-10">
            <PageMasthead
              title="Reco"
              tagline="Effortlessly reconstruct your captures into 3D spatial models"
            />
          </div>

          {/*
            * The chat stops short of the footer rather than running into it: a
            * composer at ~93dvh reads as falling off the screen. How much room
            * to give away scales with the *height* of the window, not its width
            * — Tailwind's sm:/lg: only know about width, hence the explicit
            * min-height queries. On a tall display there is space to spare; at
            * 768px every pixel spent here comes out of the transcript.
            *
            *   < 640px wide          16px   phone: the composer belongs at the bottom
            *   wide, < 800px tall     6dvh   ~46px on a 768px window
            *   wide, 800–1000px      11dvh
            *   wide, > 1000px        16dvh   ~187px at 1169px, composer near 79%
            *
            * Padding sits inside the column so the border rails still run the
            * full height to the footer.
            *
            * The bands are written as closed ranges rather than three min-height
            * thresholds because Tailwind emits arbitrary variants in its own
            * order, not source order. With open-ended thresholds a 1169px window
            * matches both the 800px and the 1000px rule, and whichever Tailwind
            * happens to print last wins — which silently gave tall displays the
            * middle value. Non-overlapping ranges mean exactly one ever applies.
            */}
          <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-5 sm:px-6 sm:[@media(max-height:799.98px)]:pb-[6dvh] sm:[@media(min-height:800px)_and_(max-height:999.98px)]:pb-[11dvh] sm:[@media(min-height:1000px)]:pb-[16dvh]">
            <ReconstructionAgent variant="inline" />
          </div>
        </div>
      </main>

      <SiteFooter />

      <EnterpriseSolutionModal
        isOpen={showEnterpriseModal}
        onClose={() => setShowEnterpriseModal(false)}
      />
    </div>
  );
};

export default ChatWithRecoPage;
